import classNames from 'classnames';
import React, { useContext, useRef, useState } from 'react';
import formatDuration from '../../../utilities/formatDuration';
import { DragType } from '../../../models/dragTypes';
import MixcloudCloudcast from '../../../models/mixcloud/mixcloudCloudcast';
import PlaylistItem from '../../../models/playlistItem/playlistItem';
import Source, { Sources } from '../../../models/source';
import Track from '../../../models/track';
import MixcloudTrack from '../../../models/track/mixcloudTrack';
import TrackLength from '../../../models/track/trackLength';
import { ActivePlaylistContext } from '../../../services/activePlaylist/activePlaylist';
import PlaylistColumns, {
  ColumnKey,
  ColumnScope,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { Context } from '../../../services/_globalContext/context';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import AddToPlaylistSubMenu from '../../common/addToPlaylist/addToPlaylistSubMenu';
import FadilaMenuItem from '../../common/contextMenu/fadilaMenuItem';
import useColumnSettings from '../../common/hooks/useColumnSettings';
import useSelectedItemsRef from '../../common/hooks/useSelectedItemsRef';
import s from '../../common/itemRow/itemRow.module.scss';
import ItemRowWrapper from '../../common/itemRow/ItemRowWrapper';
import { openOnMixcloud } from '../openOnMixcloud';

export interface MixcloudShowRowProps {
  /** Item array index */
  index: number;
  item: MixcloudCloudcast;
  handlePlayShow(item: MixcloudCloudcast): void;
  addItemsThenToLibrary(
    addItems: (i: PlaylistItem[]) => void,
  ): (...items: MixcloudCloudcast[]) => void;

  isSelected: boolean;
  selectedItemsRef: React.MutableRefObject<Iterable<MixcloudCloudcast>>;

  handleToggleItem(plItm: MixcloudCloudcast): void;
  changeItemSelectionState(
    plItm: MixcloudCloudcast,
    selectState: boolean,
    removePrevious?: boolean,
  ): void;
  handleShiftSelect(plItm: MixcloudCloudcast): void;
  clearSelected(): void;

  portalTarget?: HTMLElement;
}

/**
 * One show in the Mixcloud search results. The SoundCloud row with
 * Mixcloud's shapes: the key where the URN was, seconds where the
 * milliseconds were, and the first tag standing in for a genre Mixcloud
 * does not have.
 */
export default function ShowSearchResult({
  index,
  item,
  handlePlayShow,
  addItemsThenToLibrary,

  portalTarget,
  isSelected,
  selectedItemsRef,
  changeItemSelectionState,
  handleShiftSelect,
  handleToggleItem,
  clearSelected,
}: MixcloudShowRowProps) {
  const titleElRef = useRef<HTMLTableDataCellElement>(null);
  const selectedItemsRefWithMapping = useSelectedItemsRef<MixcloudCloudcast, MixcloudTrack>(
    MixcloudTrack.from,
    selectedItemsRef,
  );
  const ctx = useContext(Context);
  const apCtx = useContext(ActivePlaylistContext);

  const [isDragging, setIsDragging] = useState(false);

  const { columns, columnSettings } = useColumnSettings(ColumnScope.MixcloudShowSearch);

  const onDragStart = (ev: React.DragEvent) => {
    console.debug('dragstart on show item');

    setIsDragging(true);

    // We set loads of data here since you can't call getData in onDragOver
    ev.dataTransfer.setData('dragType', DragType.TrackItems);
    // types.includes checks for matching drag type in onDragOver
    ev.dataTransfer.setData(DragType.TrackItems, DragType.TrackItems);
    ev.dataTransfer.setData('text/plain', item.key);
    ev.dataTransfer.setData('name', item.name);

    ev.dataTransfer.effectAllowed = 'copy';

    if (titleElRef.current) {
      ev.dataTransfer.setDragImage(titleElRef.current, 0, 0);
    }
    if (!isSelected) {
      clearSelected();
      changeItemSelectionState(item, true, true);
    }

    ctx.dndData[item.key] = selectedItemsRefWithMapping;
  };
  function onDragEnd(_ev: React.DragEvent) {
    setIsDragging(false);

    delete ctx.dndData[item.key];
  }

  return (
    <ItemRowWrapper<MixcloudCloudcast>
      index={index}
      item={item}
      itemId={item.key}

      onDragStart={onDragStart}
      onDragEnd={onDragEnd}

      isSelected={isSelected}
      isDragging={isDragging}

      handleShiftSelect={() => handleShiftSelect(item)}
      handleToggleItem={() => handleToggleItem(item)}
      selectOnlyItem={() => changeItemSelectionState(item, true, true)}

      handlePlay={() => handlePlayShow(item)}

      menuItems={
        <>
          <FadilaMenuItem
            onClick={preventDefaultStopPropogation(() => handlePlayShow(item))}
            attributes={{
              className: 'secondary',
            }}
          >
            Play
          </FadilaMenuItem>
          <AddToPlaylistSubMenu
            newPlaylistName={item.name}
            playlistItemOrItems={(addItems) =>
              addItemsThenToLibrary(addItems)(...selectedItemsRef.current)
            }
          />
          <FadilaMenuItem onClick={preventDefaultStopPropogation(() => openOnMixcloud(item.url))}>
            Open on Mixcloud
          </FadilaMenuItem>
        </>
      }
      portalTarget={portalTarget}
    >
      {columnSettings && (
        <>
          <div className={classNames(s.rowCell, s.playBtn)}>
            {apCtx.currentTrack.id === item.key ? (
              '♫'
            ) : (
              <button
                className="play-button invisible-control"
                onClick={preventDefaultStopPropogation(() => handlePlayShow(item))}
              >
                ►
              </button>
            )}
          </div>
          {columns.map((col) => {
            const colKey = col as ColumnKey;

            const track = MixcloudTrack.from(item);

            let value: string | undefined;

            switch (colKey) {
              case TrackProperty.length:
                // A show still being processed has no audio_length, and an
                // empty cell says that better than 0:00 would.
                value =
                  track.length === TrackLength.UNAVAILABLE ? '' : formatDuration(track.length);
                break;

              case TrackProperty.title:
                value = item.name;
                break;

              case TrackProperty.artist:
                value = track.artist;
                break;

              case TrackProperty.album:
                value = '';
                break;

              // The account that posted the show. Same as the artist column
              // unless Mixcloud names hosts, which is why it is hidden until
              // asked for.
              case TrackProperty.uploader:
                value = track.uploader || '';
                break;

              case TrackProperty.genre:
                value = track.genre;
                break;

              case TrackProperty.releaseDate:
                value = Track.format(track.releaseDate, TrackProperty.releaseDate);
                break;

              case TrackProperty.type:
                value = Sources[Source.Mixcloud].name;
                break;

              case TrackProperty.dateAdded:
                value = '';
                break;

              default:
                throw new Error('Unexpected column mismatch!');
            }

            return (
              <div
                key={colKey}
                // The header cells carry this already, for the column menu.
                // On the body cells it is what the browser tests select by -
                // every class here is a hashed CSS module name.
                data-column-name={colKey}
                className={classNames(s.rowCell, {
                  [s.rightAlignCell]: PlaylistColumns[colKey].alignRight,
                })}
                ref={colKey === TrackProperty.title ? titleElRef : undefined}
                style={{
                  width: columnSettings[colKey]?.width,
                }}
                title={value}
              >
                {value}
              </div>
            );
          })}
        </>
      )}
    </ItemRowWrapper>
  );
}
